import styled from "styled-components";
import { space } from "styled-system";
import { Box, BoxProps } from "../Box";
import { Label } from "../Label";
import { Paragraph } from "../Paragraph";
import { stripHtmlTags } from "../../utils/string";

const InputBase = styled.textarea`
  ${({ hasError, disabled, theme: { radii, colors, space, fontSizes } }: any) => `
        resize: none;
        width: 100%;
        box-sizing: border-box;
        border-radius: ${radii[0]};
        border: 1px solid ${hasError ? colors.red.primary : colors.gray[30]};
        font-family: "Inter", sans-serif;
        color: ${colors.blue[100]};
        font-size: ${fontSizes[1]};
        padding: ${space[2]};
        transition: border-color .15s ease, box-shadow .15s ease;

        ::placeholder {
            color: ${colors.gray[50]};
        }
        ${
          disabled
            ? `
            cursor: not-allowed;
          `
            : ""
        }

          ::placeholder {
              color: ${colors.gray[50]};
          }

          :hover {
              ${
                hasError
                  ? ""
                  : `
                  border: 1px solid ${colors.gray[40]};
              `
              }
          }

          :focus, :focus-visible {
              outline: none;
              ${
                hasError
                  ? `border-color: ${colors.red.primary};
                     box-shadow: 0 0 0 3px ${colors.red[50]};`
                  : `border-color: ${colors.blue.primary};
                     box-shadow: 0 0 0 3px ${colors.blue[50]};`
              }
          }
      `}

  ${space}
`;

export interface TextAreaProps extends BoxProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  type?: "text" | "password" | "search" | "number";
  hasError?: boolean;
  errorMessage?: string;
  placeholder?: string;
  label?: string;
  name?: string;
  size?: "small" | "regular";
  color?: string;
  min?: number;
  max?: number;
  rows?: number;
  isDisabled?: boolean;
  isRequired?: boolean;
  /** Native tooltip — useful for explaining why a disabled field is disabled. */
  title?: string;
}

export const TextArea = (props: TextAreaProps) => {
  const {
    onChange,
    onBlur,
    type = "text",
    label,
    value,
    errorMessage,
    hasError,
    size = "regular",
    placeholder,
    name,
    pl,
    min,
    max,
    rows,
    isDisabled,
    isRequired,
    ...styleProps
  } = props;

  const inputProps = {
    errorMessage,
    hasError,
    buttonSize: size,
    type,
    placeholder,
    name,
    value,
    pl,
    min,
    max,
    rows,
    disabled: isDisabled,
    onBlur,
  } as any;

  const handleChange = (event: any) => {
    const sanitizedValue = stripHtmlTags(event.target.value);

    if (sanitizedValue === event.target.value) {
      onChange(event);
      return;
    }

    const nextEvent = {
      ...event,
      target: {
        name: event.target.name ?? name,
        id: event.target.id,
        value: sanitizedValue,
      },
      currentTarget: {
        name: event.currentTarget?.name ?? name,
        id: event.currentTarget?.id,
        value: sanitizedValue,
      },
    };

    onChange(nextEvent as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <Box position="relative" {...styleProps}>
      {label && (
        <Label>
          {label}
          {isRequired ? " *" : ""}
        </Label>
      )}

      <InputBase onChange={handleChange} {...inputProps} />

      {errorMessage && (
        <Paragraph color="red.primary" fontSize="1" mt="1" textAlign="right">
          {errorMessage}
        </Paragraph>
      )}
    </Box>
  );
};
